import React, { useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  ClockIcon,
  UserGroupIcon,
  CubeIcon,
  TagIcon,
  BuildingStorefrontIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  TruckIcon,
  ShieldExclamationIcon,
  ReceiptPercentIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  SparklesIcon,
  FolderIcon,
  RectangleStackIcon,
  BriefcaseIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';
import { useCustomers } from '../hooks/useCustomers';
import { useProducts } from '../hooks/useProducts';
import { useWarehouses } from '../hooks/useWarehouses';
import { useInventory } from '../hooks/useInventory';
import { useCategories } from '../hooks/useCategories';
import { usePermissions } from '../hooks/usePermissions';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useUsers } from '../hooks/useUsers';
import { useStorage } from '../hooks/useStorage';
import { useQuotations } from '../hooks/useQuotations';
import { useTasks } from '../hooks/useTasks';
import { leadService } from '../services/leadService';
import { Lead } from '../types/lead';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const DashboardModern = () => {
  const { customers } = useCustomers();
  const { products } = useProducts();
  const { warehouses } = useWarehouses();
  const { inventory } = useInventory();
  const { categories } = useCategories();
  const { hasAccess } = usePermissions();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { users } = useUsers();
  const { getCostOptimizationData } = useStorage();
  const { quotations } = useQuotations();
  const { tasks } = useTasks();
  const [storageData, setStorageData] = React.useState<any>(null);
  const [leads, setLeads] = React.useState<Lead[]>([]);

  React.useEffect(() => {
    if (!user?.organizationId) return;
    leadService.getLeads(user.organizationId).then(setLeads).catch(() => setLeads([]));
  }, [user?.organizationId]);

  // Load storage data
  React.useEffect(() => {
    const loadStorageData = async () => {
      try {
        const data = await getCostOptimizationData(user?.organizationId);
        setStorageData(data);
      } catch (error) {
        console.error('Error loading storage data:', error);
      }
    };
    if (user?.organizationId) {
      loadStorageData();
    }
  }, [user?.organizationId, getCostOptimizationData]);

  // Greeting logic
  const currentHour = new Date().getHours();
  let greeting = t('dashboard.welcome.afternoon');
  
  if (currentHour < 12) {
    greeting = t('dashboard.welcome.morning');
  } else if (currentHour < 18) {
    greeting = t('dashboard.welcome.afternoon');
  } else {
    greeting = t('dashboard.welcome.evening');
  }

  // Quick navigation tiles configuration
  const allQuickNavTiles = [
    {
      name: t('nav.customers'),
      href: '/customers',
      icon: UserGroupIcon,
      color: 'from-blue-500 to-blue-600',
      count: customers.length,
      module: 'customers'
    },
    {
      name: t('nav.leads'),
      href: '/leads',
      icon: SparklesIcon,
      color: 'from-primary-500 to-primary-600',
      count: null,
      module: 'customers'
    },
    {
      name: t('nav.suppliers'),
      href: '/suppliers',
      icon: BuildingStorefrontIcon,
      color: 'from-emerald-500 to-emerald-600',
      count: null,
      module: 'suppliers'
    },
    {
      name: t('nav.products'),
      href: '/products',
      icon: CubeIcon,
      color: 'from-orange-500 to-orange-600',
      count: products.length,
      module: 'products'
    },
    {
      name: t('nav.categories'),
      href: '/categories',
      icon: TagIcon,
      color: 'from-purple-500 to-purple-600',
      count: categories.length,
      module: 'products'
    },
    {
      name: t('nav.warehouse'),
      href: '/warehouse',
      icon: BuildingStorefrontIcon,
      color: 'from-indigo-500 to-indigo-600',
      count: warehouses.filter(w => w.isActive).length,
      module: 'inventory'
    },
    {
      name: t('nav.inventory'),
      href: '/inventory',
      icon: ClipboardDocumentListIcon,
      color: 'from-teal-500 to-teal-600',
      count: inventory.length,
      module: 'inventory'
    },
    {
      name: t('nav.sales'),
      href: '/sales',
      icon: ShoppingCartIcon,
      color: 'from-green-500 to-green-600',
      count: null,
      module: 'quotations'
    },
    {
      name: t('nav.quotations'),
      href: '/quotations',
      icon: DocumentTextIcon,
      color: 'from-rose-500 to-rose-600',
      count: null,
      module: 'quotations'
    },
    {
      name: t('nav.orders'),
      href: '/orders',
      icon: ShoppingCartIcon,
      color: 'from-blue-500 to-blue-600',
      count: null,
      module: 'quotations'
    },
    {
      name: t('nav.delivery_notes'),
      href: '/delivery-notes',
      icon: TruckIcon,
      color: 'from-indigo-500 to-indigo-600',
      count: null,
      module: 'quotations'
    },
    {
      name: t('nav.warranty'),
      href: '/warranty',
      icon: ShieldExclamationIcon,
      color: 'from-yellow-500 to-yellow-600',
      count: null,
      module: 'products'
    },
    {
      name: t('nav.billing'),
      href: '/billing',
      icon: ReceiptPercentIcon,
      color: 'from-red-500 to-red-600',
      count: null,
      module: 'billing'
    },
    {
      name: t('nav.deals'),
      href: '/deals',
      icon: CurrencyDollarIcon,
      color: 'from-pink-500 to-pink-600',
      count: null,
      module: 'deals'
    },
    {
      name: t('nav.contracts'),
      href: '/contracts',
      icon: DocumentTextIcon,
      color: 'from-violet-500 to-violet-600',
      count: null,
      module: 'contracts'
    },
    {
      name: t('nav.worksheets'),
      href: '/worksheets',
      icon: ClipboardDocumentListIcon,
      color: 'from-slate-500 to-slate-600',
      count: null,
      module: 'worksheets'
    },
    {
      name: t('nav.tasks'),
      href: '/tasks',
      icon: ClipboardDocumentListIcon,
      color: 'from-cyan-500 to-cyan-600',
      count: null,
      module: 'tasks'
    },
    {
      name: t('nav.templates'),
      href: '/templates',
      icon: DocumentTextIcon,
      color: 'from-amber-500 to-amber-600',
      count: null,
      module: 'templates'
    },
    {
      name: t('nav.ai_quotations'),
      href: '/ai-quotations',
      icon: SparklesIcon,
      color: 'from-purple-500 to-purple-600',
      count: null,
      module: 'quotations'
    },
    {
      name: t('nav.documentStorage'),
      href: '/document-storage',
      icon: FolderIcon,
      color: 'from-blue-500 to-blue-600',
      count: null,
      module: 'customers'
    },
    {
      name: t('nav.formBuilder'),
      href: '/form-builder',
      icon: RectangleStackIcon,
      color: 'from-indigo-500 to-indigo-600',
      count: null,
      module: 'forms'
    },
    {
      name: 'Flow Builder',
      href: '/flow-builder',
      icon: Cog6ToothIcon,
      color: 'from-violet-500 to-violet-600',
      count: null,
      module: 'forms'
    },
    {
      name: t('nav.myForms'),
      href: '/my-forms',
      icon: ListBulletIcon,
      color: 'from-teal-500 to-teal-600',
      count: null,
      module: 'forms'
    },
    {
      name: t('nav.projects'),
      href: '/projects',
      icon: BriefcaseIcon,
      color: 'from-emerald-500 to-emerald-600',
      count: null,
      module: 'projects'
    },
    {
      name: t('nav.reports'),
      href: '/reports',
      icon: ChartBarIcon,
      color: 'from-red-500 to-red-600',
      count: null,
      module: 'reports'
    },
    {
      name: t('nav.settings'),
      href: '/settings',
      icon: Cog6ToothIcon,
      color: 'from-gray-500 to-gray-600',
      count: null,
      module: 'settings'
    }
  ];

  if (user?.role === 'super_admin') {
    allQuickNavTiles.push({
      name: t('nav.system_admin'),
      href: '/system-admin',
      icon: ShieldCheckIcon,
      color: 'from-cyan-500 to-cyan-600',
      count: null,
      module: 'admin'
    });
  }

  const quickNavTiles = allQuickNavTiles.filter(tile => {
    return hasAccess(tile.module as keyof import('../types/user').UserModulePermissions);
  });

  // Inventory stats
  const inventoryStats = useMemo(() => {
    const totalItems = inventory.length;
    const lowStockItems = inventory.filter(item => 
      item.minQuantity && item.quantity < item.minQuantity
    ).length;
    const outOfStockItems = inventory.filter(item => item.quantity === 0).length;
    const totalValue = inventory.reduce((sum, item) => {
      const product = products.find(p => p.id === item.productId);
      return sum + (item.quantity * (product?.purchasePrice || 0));
    }, 0);

    return {
      totalItems,
      lowStockItems,
      outOfStockItems,
      totalValue
    };
  }, [inventory, products]);

  // Recent customers
  const recentCustomers = useMemo(() => {
    return [...customers]
      .sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.getTime() - a.createdAt.getTime();
        }
        return 0;
      })
      .slice(0, 5);
  }, [customers]);

  // Recent products
  const recentProducts = useMemo(() => {
    return [...products]
      .sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.getTime() - a.createdAt.getTime();
        }
        return 0;
      })
      .slice(0, 5);
  }, [products]);

  // Low stock products
  const lowStockProducts = useMemo(() => {
    return inventory
      .filter(item => item.minQuantity && item.quantity < item.minQuantity)
      .sort((a, b) => {
        const aRatio = a.quantity / (a.minQuantity || 1);
        const bRatio = b.quantity / (b.minQuantity || 1);
        return aRatio - bRatio;
      })
      .slice(0, 5);
  }, [inventory]);

  // Real database growth data - last 6 months
  const databaseGrowthData = useMemo(() => {
    const now = new Date();
    const monthsData = [];
    
    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      const monthName = monthDate.toLocaleDateString('hu-HU', { month: 'short' });
      
      // Count items created in this month
      const customersInMonth = customers.filter(c => {
        if (!c.createdAt) return false;
        return c.createdAt >= monthDate && c.createdAt <= monthEnd;
      }).length;
      
      const productsInMonth = products.filter(p => {
        if (!p.createdAt) return false;
        return p.createdAt >= monthDate && p.createdAt <= monthEnd;
      }).length;
      
      const inventoryInMonth = inventory.filter(inv => {
        // Inventory items may not have createdAt, skip them
        return false; // TODO: Add createdAt to Inventory type if needed
      }).length;
      
      monthsData.push({
        name: monthName.charAt(0).toUpperCase() + monthName.slice(1),
        customers: customersInMonth,
        products: productsInMonth,
        inventory: inventoryInMonth,
        total: customersInMonth + productsInMonth + inventoryInMonth
      });
    }
    
    return monthsData;
  }, [customers, products, inventory]);

  // No organization check
  if (!user?.organizationId && user?.role !== 'super_admin') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="mx-auto h-24 w-24 text-blue-500 mb-8">
            <UserGroupIcon className="h-full w-full" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Üdvözöljük, {user?.displayName}!
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            A fiókja beállítás alatt van. Kérjük, vegye fel a kapcsolatot a rendszergazdával a hozzáférés aktiválásához.
          </p>
        </div>
      </div>
    );
  }

  // Real system info from hooks
  const totalStorageGB = storageData ? storageData.totalSize / (1024 * 1024 * 1024) : 0;
  const activeUsers = users.filter(u => {
    // Aktív felhasználók: akik az elmúlt 30 napban bejelentkeztek
    if (!u.lastLoginAt) return false;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return u.lastLoginAt >= thirtyDaysAgo;
  }).length;
  
  const systemInfo = {
    storage: {
      used: totalStorageGB,
      total: 100, // Ez lehet egy package limit a jövőben
      unit: 'GB'
    },
    users: {
      active: activeUsers,
      total: users.length
    },
    database: {
      records: customers.length + products.length + inventory.length + users.length,
      size: totalStorageGB
    },
    uptime: '99.9%' // Ez egy külső monitoring service-ből jöhetne
  };


  const fmtHuf = (v: number) =>
    new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 }).format(v || 0);

  // ---- KPI-k: ami dönteni segít, nem puszta darabszám ----
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000);
  const in3Days = new Date(startOfToday.getTime() + 3 * 24 * 60 * 60 * 1000);
  const weekAgo = new Date(startOfToday.getTime() - 7 * 24 * 60 * 60 * 1000);

  const newCustomersThisWeek = customers.filter(c => c.createdAt && c.createdAt >= weekAgo).length;
  const openQuotations = quotations.filter(q => q.status === 'sent' || q.status === 'pending');
  const expiringQuotations = openQuotations.filter(q => {
    const d = q.validUntil ? new Date(q.validUntil) : null;
    return d && !isNaN(d.getTime()) && d >= startOfToday && d <= in3Days;
  }).length;
  const openTasks = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
  const todayTasks = openTasks.filter(t => t.dueDate && t.dueDate < endOfToday);
  const overdueTasks = openTasks.filter(t => t.dueDate && t.dueDate < startOfToday).length;
  const upcomingTasks = [...openTasks]
    .filter(t => t.dueDate)
    .sort((a, b) => (a.dueDate!.getTime() - b.dueDate!.getTime()))
    .slice(0, 5);

  const openLeads = leads.filter(l => l.status !== 'converted' && l.status !== 'unqualified');
  const leadsDueToday = openLeads.filter(l => l.nextFollowUpAt && l.nextFollowUpAt < endOfToday).length;
  const newLeadsThisWeek = leads.filter(l => l.createdAt && l.createdAt >= weekAgo).length;

  const kpis = [
    { label: 'Nyitott leadek', value: openLeads.length, sub: leadsDueToday > 0 ? `${leadsDueToday} követés ma` : (newLeadsThisWeek > 0 ? `+${newLeadsThisWeek} ezen a héten` : 'nincs mai követés'), href: '/leads', warn: leadsDueToday > 0 },
    { label: t('nav.customers'), value: customers.length, sub: newCustomersThisWeek > 0 ? `+${newCustomersThisWeek} ezen a héten` : 'nincs új ezen a héten', href: '/customers' },
    { label: 'Nyitott árajánlat', value: openQuotations.length, sub: expiringQuotations > 0 ? `${expiringQuotations} lejár 3 napon belül` : 'nincs lejáró', href: '/quotations' },
    { label: 'Mai feladatok', value: todayTasks.length, sub: overdueTasks > 0 ? `${overdueTasks} késésben` : 'nincs késésben', href: '/tasks', warn: overdueTasks > 0 },
    { label: 'Alacsony készlet', value: inventoryStats.lowStockItems, sub: inventoryStats.lowStockItems > 0 ? 'termék minimum alatt' : 'minden készleten van', href: '/inventory', warn: inventoryStats.lowStockItems > 0 },
  ];

  // ---- Modulok csoportosítva, csoportonként egy halvány színkód ----
  const groups: { id: string; name: string; hrefs: string[]; icon: string; iconBg: string }[] = [
    { id: 'crm', name: 'CRM & Kapcsolatok', hrefs: ['/customers', '/leads', '/suppliers', '/deals'], icon: 'text-primary-700 dark:text-primary-300', iconBg: 'bg-primary-50 dark:bg-primary-500/15' },
    { id: 'prod', name: 'Termékek & Készlet', hrefs: ['/products', '/categories', '/warehouse', '/inventory', '/warranty'], icon: 'text-sky-800 dark:text-sky-300', iconBg: 'bg-sky-50 dark:bg-sky-500/15' },
    { id: 'sales', name: 'Értékesítés & Pénzügyek', hrefs: ['/sales', '/quotations', '/ai-quotations', '/orders', '/delivery-notes', '/billing', '/contracts'], icon: 'text-amber-800 dark:text-amber-300', iconBg: 'bg-amber-50 dark:bg-amber-500/15' },
    { id: 'docs', name: 'Dokumentumok & Folyamatok', hrefs: ['/templates', '/document-storage', '/form-builder', '/my-forms', '/flow-builder'], icon: 'text-violet-800 dark:text-violet-300', iconBg: 'bg-violet-50 dark:bg-violet-500/15' },
    { id: 'work', name: 'Munka & Rendszer', hrefs: ['/projects', '/tasks', '/worksheets', '/reports', '/settings', '/system-admin'], icon: 'text-gray-700 dark:text-gray-300', iconBg: 'bg-gray-100 dark:bg-gray-800' },
  ];
  const grouped = groups
    .map(g => ({ ...g, tiles: quickNavTiles.filter(tile => g.hrefs.includes(tile.href)) }))
    .filter(g => g.tiles.length > 0);
  const ungrouped = quickNavTiles.filter(tile => !groups.some(g => g.hrefs.includes(tile.href)));
  if (ungrouped.length > 0) grouped.push({ id: 'other', name: 'Egyéb', hrefs: [], icon: 'text-gray-700 dark:text-gray-300', iconBg: 'bg-gray-100 dark:bg-gray-800', tiles: ungrouped });

  const dateLine = now.toLocaleDateString('hu-HU', { weekday: 'long', month: 'long', day: 'numeric' });
  const hasAnyData = customers.length + products.length + inventory.length > 0;

  const fmtDue = (d: Date) => {
    if (d < startOfToday) return { text: `késik · ${format(d, 'MM.dd.')}`, late: true };
    if (d < endOfToday) return { text: `ma ${format(d, 'HH:mm')}`, late: false };
    if (d < new Date(endOfToday.getTime() + 24 * 60 * 60 * 1000)) return { text: 'holnap', late: false };
    return { text: format(d, 'MM.dd.'), late: false };
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-7 space-y-6 pb-20 sm:pb-8">

      {/* Fejléc: dátum, üdvözlés, keresés, fő művelet */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{dateLine}</p>
          <h1 className="mt-0.5 text-[26px] font-semibold text-gray-900 dark:text-gray-50 tracking-[-0.02em] leading-tight">
            {greeting}, {user?.displayName?.split(' ').slice(-1)[0] || t('dashboard.welcome.user')}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('openCommandPalette'))}
            className="hidden sm:flex items-center gap-2 h-9 w-60 px-3 rounded-lg border border-[var(--crm-border)] bg-[var(--crm-surface)] text-sm text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
          >
            <MagnifyingGlassIcon className="w-4 h-4" />
            <span>Keresés…</span>
            <kbd className="ml-auto text-[11px] px-1.5 py-0.5 rounded border border-[var(--crm-border)] font-sans">⌘K</kbd>
          </button>
          <Link to="/customers" onClick={() => setTimeout(() => window.dispatchEvent(new CustomEvent('openCustomerForm')), 50)} className="crm-btn crm-btn-primary h-9">
            <PlusIcon className="w-4 h-4" />
            Új ügyfél
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">

        {/* Bal: KPI-k + modulok */}
        <div className="space-y-6 min-w-0">

          {/* KPI sor – keret nélkül, hajszálvonalakkal */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-4 py-5 border-y border-[var(--crm-border)]">
            {kpis.map((k, i) => (
              <Link key={k.href} to={k.href} className={`block min-w-0 group ${i > 0 ? 'md:border-l md:border-[var(--crm-border)] md:pl-6' : ''}`}>
                <div className="text-[12.5px] text-gray-500 dark:text-gray-400 truncate">{k.label}</div>
                <div className="text-[34px] leading-[1.15] font-semibold tracking-[-0.03em] tabular-nums text-gray-900 dark:text-gray-50 group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors">{k.value}</div>
                <div className={`text-xs mt-0.5 truncate ${k.warn ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>{k.sub}</div>
              </Link>
            ))}
          </div>

          {/* Modulok csoportokban */}
          <div className="space-y-5">
            {grouped.map(g => (
              <div key={g.id}>
                <div className="text-[11.5px] font-semibold tracking-[0.06em] uppercase text-gray-500 dark:text-gray-400 mb-2 ml-0.5">{g.name}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {g.tiles.map(tile => (
                    <Link
                      key={tile.href}
                      to={tile.href}
                      className="flex items-center gap-2.5 px-3 py-2.5 crm-card hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-all min-w-0"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${g.iconBg} ${g.icon}`}>
                        <tile.icon className="w-[17px] h-[17px]" />
                      </div>
                      <span className="text-[13px] font-medium text-gray-900 dark:text-gray-100 truncate">{tile.name}</span>
                      {tile.count !== null && tile.count > 0 && (
                        <span className="ml-auto text-xs text-gray-400 tabular-nums flex-shrink-0">{tile.count}</span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Jobb sáv */}
        <div className="space-y-4 min-w-0">

          {/* Mai teendők */}
          <section className="crm-card overflow-hidden">
            <header className="flex items-center justify-between px-4 py-3 border-b border-[var(--crm-border)]">
              <h2 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Teendők</h2>
              <Link to="/tasks" className="text-xs text-primary-600 dark:text-primary-400 hover:underline">Feladatok →</Link>
            </header>
            {upcomingTasks.length > 0 ? (
              <ul className="divide-y divide-[var(--crm-border)]">
                {upcomingTasks.map(task => {
                  const due = fmtDue(task.dueDate!);
                  return (
                    <li key={task.id}>
                      <Link to="/tasks" className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                        <span className="w-4 h-4 rounded border-[1.5px] border-gray-300 dark:border-gray-600 flex-shrink-0" />
                        <span className="flex-1 min-w-0 text-[13px] text-gray-900 dark:text-gray-100 truncate">{task.title}</span>
                        <span className={`flex items-center gap-1 text-xs flex-shrink-0 ${due.late ? 'text-red-700 dark:text-red-400' : 'text-gray-400'}`}>
                          <ClockIcon className="w-3.5 h-3.5" />{due.text}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyState icon={CheckCircleIcon} text="Nincs határidős feladat." tone="ok" compact action={{ to: '/tasks', label: t('nav.tasks') }} />
            )}
          </section>

          {/* Legutóbbi ügyfelek */}
          <section className="crm-card overflow-hidden">
            <header className="flex items-center justify-between px-4 py-3 border-b border-[var(--crm-border)]">
              <h2 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">{t('dashboard.recentCustomers')}</h2>
              <Link to="/customers" className="text-xs text-primary-600 dark:text-primary-400 hover:underline">Összes →</Link>
            </header>
            {recentCustomers.length > 0 ? (
              <ul className="divide-y divide-[var(--crm-border)]">
                {recentCustomers.map(customer => (
                  <li key={customer.id}>
                    <Link to={`/customers?id=${customer.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                      <div className="w-[30px] h-[30px] rounded-full bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                        {(customer.type === 'organization' ? customer.companyName : customer.lastName)?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-medium text-gray-900 dark:text-gray-100 truncate">
                          {customer.type === 'organization' ? customer.companyName : `${customer.lastName} ${customer.firstName}`}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{customer.email}</p>
                      </div>
                      {customer.createdAt && <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">{format(customer.createdAt, 'MM.dd.')}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState icon={UserGroupIcon} text={t('dashboard.noCustomers')} compact action={{ to: '/customers', label: t('nav.customers') }} />
            )}
          </section>

          {/* Alacsony készlet – csak ha van */}
          {lowStockProducts.length > 0 && (
            <section className="crm-card overflow-hidden">
              <header className="flex items-center justify-between px-4 py-3 border-b border-[var(--crm-border)]">
                <h2 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">{t('dashboard.lowStockProducts')}</h2>
                <Link to="/inventory" className="text-xs text-primary-600 dark:text-primary-400 hover:underline">Készlet →</Link>
              </header>
              <ul className="divide-y divide-[var(--crm-border)]">
                {lowStockProducts.map(item => {
                  const ratio = item.minQuantity ? Math.min(item.quantity / item.minQuantity, 1) : 0;
                  const zero = item.quantity === 0;
                  return (
                    <li key={item.id} className="px-4 py-2.5">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-gray-900 dark:text-gray-100 truncate">{item.productName}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.warehouseName}</p>
                        </div>
                        <span className={`text-[13px] font-medium tabular-nums flex-shrink-0 ${zero ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          {item.quantity} / {item.minQuantity} {item.unit}
                        </span>
                      </div>
                      <div className="mt-2 h-[3px] rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                        <div className={`h-full rounded-full ${zero ? 'bg-red-500' : 'bg-amber-500'}`} style={{ width: `${Math.max(ratio * 100, 3)}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Új rekordok – 6 hónap */}
          <section className="crm-card overflow-hidden">
            <header className="px-4 py-3 border-b border-[var(--crm-border)]">
              <h2 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Új rekordok – 6 hónap</h2>
            </header>
            <div className="p-3">
              {hasAnyData ? (
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={databaseGrowthData} barCategoryGap={10}>
                    <CartesianGrid vertical={false} stroke="var(--crm-border)" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} />
                    <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={22} tick={{ fontSize: 11, fill: '#9ca3af' }} />
                    <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={{ borderRadius: 8, border: '1px solid var(--crm-border)', background: 'var(--crm-surface)', fontSize: 12 }} />
                    <Bar dataKey="customers" name={t('nav.customers')} stackId="a" fill="#0f7f75" />
                    <Bar dataKey="products" name={t('nav.products')} stackId="a" fill="#6fd8c8" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState icon={ChartBarIcon} text="Amint rögzítesz ügyfelet vagy terméket, itt látod a növekedést." compact />
              )}
            </div>
          </section>

          {/* Rendszer – tömören */}
          <div className="px-1 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-4 gap-y-1 tabular-nums">
            <span>Tárhely {systemInfo.storage.used.toFixed(2)} / {systemInfo.storage.total} {systemInfo.storage.unit}</span>
            <span>Aktív {systemInfo.users.active} / {systemInfo.users.total} felhasználó</span>
            <span>{systemInfo.database.records.toLocaleString('hu-HU')} rekord</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Egységes üres állapot a dashboard kártyáihoz */
const EmptyState: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  text: string;
  action?: { to: string; label: string };
  tone?: 'default' | 'ok';
  compact?: boolean;
}> = ({ icon: Icon, text, action, tone = 'default', compact }) => (
  <div className={`flex flex-col items-center justify-center text-center ${compact ? 'py-6' : 'py-10'} px-4`}>
    <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-2.5 ${
      tone === 'ok' ? 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
    }`}>
      <Icon className="w-[18px] h-[18px]" />
    </div>
    <p className="text-[13px] text-gray-500 dark:text-gray-400 max-w-xs">{text}</p>
    {action && (
      <Link to={action.to} className="mt-2 text-[13px] font-medium text-primary-600 dark:text-primary-400 hover:underline">
        + {action.label}
      </Link>
    )}
  </div>
);

export default DashboardModern;
